import { Component } from '@theme/component';

/**
 * <caldera-bundle-reviews>
 *
 * Aggregated Yotpo reviews for a bundle PDP. A bundle product rarely has its own
 * reviews, so this widget fetches reviews (and Q&A) for the bundle product AND
 * each of its `global.bundle_products` components straight from Yotpo's public
 * widget API, merges them into one sorted, paginated list, and renders it in
 * brand styling — no Yotpo iframe, no per-component tabs.
 *
 * Port of the legacy `bundle-yotpo-aggregated-reviews` section: same endpoints
 * and aggregation rules (the aggregate rating excludes the parent bundle's own
 * reviews when components exist), rebuilt on the Component framework with native
 * <dialog> modals and Caldera tokens.
 *
 * Data (bundle product + components) and config (app key, page size) arrive from
 * Liquid: a JSON <script ref="data"> and data-* attributes on the host.
 *
 * @typedef {object} CalderaBundleReviewsRefs
 * @property {HTMLScriptElement} data
 * @property {HTMLElement} loading
 * @property {HTMLElement} content
 * @property {HTMLElement} errorState
 * @property {HTMLElement} reviewList
 * @property {HTMLElement} pagination
 * @property {HTMLElement} pageNumbers
 * @property {HTMLButtonElement} prevPage
 * @property {HTMLButtonElement} nextPage
 * @property {HTMLSelectElement} reviewSort
 * @property {HTMLElement} aggregateSummary
 * @property {HTMLElement} aggregateRating
 * @property {HTMLElement} aggregateCount
 * @property {HTMLElement} aggregateStars
 * @property {HTMLElement} [answerList]
 * @property {HTMLElement} [answersPagination]
 * @property {HTMLElement} [answersPageNumbers]
 * @property {HTMLButtonElement} [answersPrevPage]
 * @property {HTMLButtonElement} [answersNextPage]
 * @property {HTMLSelectElement} [answersSort]
 * @property {HTMLElement} [answersAggregateSummary]
 * @property {HTMLElement} [answersAggregateCount]
 *
 * @extends {Component<CalderaBundleReviewsRefs>}
 */
class CalderaBundleReviews extends Component {
  // Form/dialog elements live inside nested <dialog-component>s, so they aren't
  // this component's refs — they're reached via event delegation (submit/click
  // bubble up) and form-scoped queries instead.
  requiredRefs = [
    'data',
    'loading',
    'content',
    'errorState',
    'reviewList',
    'pagination',
    'pageNumbers',
    'prevPage',
    'nextPage',
    'reviewSort',
    'aggregateSummary',
    'aggregateRating',
    'aggregateCount',
    'aggregateStars',
  ];

  /** @type {AggregatedReview[]} */ #reviews = [];
  /** @type {AggregatedReview[]} */ #sortedReviews = [];
  #reviewPage = 1;

  /** @type {AggregatedQuestion[]} */ #questions = [];
  /** @type {AggregatedQuestion[]} */ #sortedQuestions = [];
  #answerPage = 1;
  #questionsLoaded = false;

  /** @type {Set<string>} */ #submittedReviewEmails = new Set();
  /** @type {Set<string>} */ #submittedQuestionEmails = new Set();

  connectedCallback() {
    super.connectedCallback();

    this.#appKey = this.dataset.appKey ?? '';
    this.#perPage = Number.parseInt(this.dataset.reviewsPerPage ?? '10', 10) || 10;
    this.#bundle = this.#parseData();

    // Delegated: clicks (tabs + star rating) and submits (both dialog forms)
    // bubble up to the host, so a single pair of listeners covers content that
    // lives inside nested <dialog-component>s. Dialog open/close is handled by
    // Horizon's dialog-component via on:click bindings.
    this.addEventListener('click', this.#onClick);
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('change', this.#onChange);
    this.refs.prevPage.addEventListener('click', this.#onPrevReviews);
    this.refs.nextPage.addEventListener('click', this.#onNextReviews);
    this.refs.answersPrevPage?.addEventListener('click', this.#onPrevAnswers);
    this.refs.answersNextPage?.addEventListener('click', this.#onNextAnswers);

    this.#loadReviews();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('change', this.#onChange);
  }

  #appKey = '';
  #perPage = 10;
  /** @type {BundleData} */ #bundle = { productId: 0, productTitle: '', childProducts: [] };

  /** @returns {BundleData} */
  #parseData() {
    try {
      const parsed = JSON.parse(this.refs.data.textContent || '{}');
      return {
        productId: typeof parsed.productId === 'number' ? parsed.productId : 0,
        productTitle: typeof parsed.productTitle === 'string' ? parsed.productTitle : '',
        childProducts: Array.isArray(parsed.childProducts) ? parsed.childProducts : [],
      };
    } catch {
      return { productId: 0, productTitle: '', childProducts: [] };
    }
  }

  /** The bundle product plus every component, deduped, as {id, title}. */
  get #products() {
    const products = [{ id: this.#bundle.productId, title: this.#bundle.productTitle }];
    const seen = new Set([this.#bundle.productId]);
    for (const child of this.#bundle.childProducts) {
      if (seen.has(child.id)) continue;
      seen.add(child.id);
      products.push({ id: child.id, title: child.title });
    }
    return products;
  }

  // --- Delegated UI events -------------------------------------------------

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-action]') : null;
    if (!trigger) return;

    switch (trigger.getAttribute('data-action')) {
      case 'rate':
        this.#rate(trigger);
        break;
      case 'tab':
        this.#selectTab(trigger.getAttribute('data-tab') ?? 'reviews');
        break;
    }
  };

  /** Sort <select> change — delegated so it covers both the review and answer sorts. @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    if (target === this.refs.reviewSort) this.#onReviewSort();
    else if (target === this.refs.answersSort) this.#onAnswerSort();
  };

  /** @param {Element} button */
  #rate(button) {
    const form = button.closest('form');
    if (!form) return;
    const rating = Number.parseInt(button.getAttribute('data-rating') || '0', 10);
    const input = form.querySelector('input[name="rating"]');
    if (input instanceof HTMLInputElement) input.value = String(rating);
    for (const star of form.querySelectorAll('[data-rating]')) {
      const value = Number.parseInt(star.getAttribute('data-rating') || '0', 10);
      star.classList.toggle('caldera-bundle-reviews__star-btn--active', value <= rating);
    }
  }

  /** @param {string} tabName */
  #selectTab(tabName) {
    for (const button of this.querySelectorAll('[data-action="tab"]')) {
      button.classList.toggle('caldera-bundle-reviews__tab--active', button.getAttribute('data-tab') === tabName);
    }
    for (const panel of this.querySelectorAll('[data-tab-panel]')) {
      panel.toggleAttribute('hidden', panel.getAttribute('data-tab-panel') !== tabName);
    }
    if (tabName === 'answers' && !this.#questionsLoaded) this.#loadAnswers();
  }

  // --- Reviews -------------------------------------------------------------

  async #loadReviews() {
    this.#setState('loading');
    try {
      this.#reviews = await fetchCombinedReviews(this.#appKey, this.#products);
      if (this.#reviews.length === 0) return this.#setState('error');

      // Aggregate rating excludes the parent bundle's own reviews when it has
      // components — but fall back to the full set so the summary (and its write
      // button) still shows when only the bundle itself has reviews.
      const childReviews =
        this.#bundle.childProducts.length > 0
          ? this.#reviews.filter((review) => review.product_id !== this.#bundle.productId)
          : this.#reviews;
      const forAggregate = childReviews.length > 0 ? childReviews : this.#reviews;

      const average = forAggregate.reduce((sum, review) => sum + review.score, 0) / forAggregate.length;
      this.#renderAggregate(average, forAggregate.length);

      this.#sortedReviews = sortReviews(this.#reviews, this.refs.reviewSort.value);
      this.#goToReviewPage(1);
      this.#setState('content');
    } catch (error) {
      console.error('[Caldera bundle reviews] Failed to load reviews:', error);
      this.#setState('error');
    }
  }

  /** @param {number} rating @param {number} count */
  #renderAggregate(rating, count) {
    this.refs.aggregateRating.textContent = rating.toFixed(1);
    this.refs.aggregateCount.textContent = `${count.toLocaleString()} ${count === 1 ? 'review' : 'reviews'}`;
    this.refs.aggregateStars.replaceChildren(...starElements(rating));
    this.refs.aggregateStars.setAttribute('aria-label', `${rating.toFixed(1)} out of 5 stars`);
    this.refs.aggregateSummary.hidden = false;
  }

  /** @param {number} page */
  #goToReviewPage(page) {
    this.#reviewPage = page;
    const totalPages = Math.ceil(this.#sortedReviews.length / this.#perPage);
    const start = (page - 1) * this.#perPage;
    const slice = this.#sortedReviews.slice(start, start + this.#perPage);

    this.refs.reviewList.replaceChildren(
      ...(slice.length ? slice.map((review) => reviewCard(review)) : [emptyMessage('No reviews yet.')])
    );

    renderPagination({
      container: this.refs.pagination,
      pageNumbers: this.refs.pageNumbers,
      prev: this.refs.prevPage,
      next: this.refs.nextPage,
      page,
      totalPages,
      onSelect: (target) => this.#goToReviewPage(target),
    });
  }

  #onReviewSort = () => {
    this.#sortedReviews = sortReviews(this.#reviews, this.refs.reviewSort.value);
    this.#goToReviewPage(1);
  };

  #onPrevReviews = () => {
    if (this.#reviewPage > 1) this.#goToReviewPage(this.#reviewPage - 1);
  };

  #onNextReviews = () => {
    const totalPages = Math.ceil(this.#sortedReviews.length / this.#perPage);
    if (this.#reviewPage < totalPages) this.#goToReviewPage(this.#reviewPage + 1);
  };

  // --- Questions & answers -------------------------------------------------

  async #loadAnswers() {
    const { answerList, answersAggregateSummary, answersAggregateCount } = this.refs;
    if (!answerList || !answersAggregateSummary || !answersAggregateCount) return;

    this.#questionsLoaded = true;
    answerList.replaceChildren(spinner());

    try {
      this.#questions = await fetchCombinedQuestions(this.#appKey, this.#products);
      if (this.#questions.length === 0) {
        answerList.replaceChildren(emptyMessage('No questions yet. Be the first to ask!'));
        return;
      }
      answersAggregateCount.textContent = `${this.#questions.length.toLocaleString()} ${
        this.#questions.length === 1 ? 'question' : 'questions'
      }`;
      answersAggregateSummary.hidden = false;
      this.#sortedQuestions = sortQuestions(this.#questions, this.refs.answersSort?.value ?? 'date-desc');
      this.#goToAnswerPage(1);
    } catch (error) {
      console.error('[Caldera bundle reviews] Failed to load questions:', error);
      answerList.replaceChildren(emptyMessage('No questions yet. Be the first to ask!'));
    }
  }

  /** @param {number} page */
  #goToAnswerPage(page) {
    const { answerList, answersPagination, answersPageNumbers, answersPrevPage, answersNextPage } = this.refs;
    if (!answerList || !answersPagination || !answersPageNumbers || !answersPrevPage || !answersNextPage) return;

    this.#answerPage = page;
    const totalPages = Math.ceil(this.#sortedQuestions.length / this.#perPage);
    const start = (page - 1) * this.#perPage;
    const slice = this.#sortedQuestions.slice(start, start + this.#perPage);

    answerList.replaceChildren(...slice.map((question) => questionCard(question)));

    renderPagination({
      container: answersPagination,
      pageNumbers: answersPageNumbers,
      prev: answersPrevPage,
      next: answersNextPage,
      page,
      totalPages,
      onSelect: (target) => this.#goToAnswerPage(target),
    });
  }

  #onAnswerSort = () => {
    this.#sortedQuestions = sortQuestions(this.#questions, this.refs.answersSort?.value ?? 'date-desc');
    this.#goToAnswerPage(1);
  };

  #onPrevAnswers = () => {
    if (this.#answerPage > 1) this.#goToAnswerPage(this.#answerPage - 1);
  };

  #onNextAnswers = () => {
    const totalPages = Math.ceil(this.#sortedQuestions.length / this.#perPage);
    if (this.#answerPage < totalPages) this.#goToAnswerPage(this.#answerPage + 1);
  };

  // --- Form submits (delegated) --------------------------------------------

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();
    if (form.matches('[data-review-form]')) this.#submitReview(form);
    else if (form.matches('[data-question-form]')) this.#submitQuestion(form);
  };

  /** @param {HTMLFormElement} form */
  async #submitReview(form) {
    const success = this.#formEl(form, 'success');
    const error = this.#formEl(form, 'error');
    const submit = this.#formEl(form, 'submit');
    if (!success || !error || !submit) return;
    success.hidden = true;
    error.hidden = true;

    const data = new FormData(form);
    const rating = Number.parseInt(String(data.get('rating') || '0'), 10);
    const title = String(data.get('title') || '').trim();
    const content = String(data.get('content') || '').trim();
    const displayName = String(data.get('display_name') || '').trim();
    const email = String(data.get('email') || '').trim().toLowerCase();

    if (rating === 0) return this.#showFormError(error, 'Please select a rating.');
    if (!title || !content || !displayName || !email) {
      return this.#showFormError(error, 'Please fill in all required fields.');
    }
    if (this.#submittedReviewEmails.has(email)) {
      return this.#showFormError(error, 'You have already submitted a review for this product.');
    }

    const restore = this.#pending(submit);
    try {
      await submitReview(this.#appKey, this.#bundle.productId, { rating, title, content, email, display_name: displayName });
      this.#submittedReviewEmails.add(email);
      success.hidden = false;
      this.#resetForm(form);
      setTimeout(() => {
        this.#closeDialog(form);
        this.#loadReviews();
      }, 2000);
    } catch (submitError) {
      this.#showFormError(error, reviewSubmitMessage(submitError, email, this.#submittedReviewEmails));
    } finally {
      restore();
    }
  }

  /** @param {HTMLFormElement} form */
  async #submitQuestion(form) {
    const success = this.#formEl(form, 'success');
    const error = this.#formEl(form, 'error');
    const submit = this.#formEl(form, 'submit');
    if (!success || !error || !submit) return;
    success.hidden = true;
    error.hidden = true;

    const data = new FormData(form);
    const content = String(data.get('content') || '').trim();
    const displayName = String(data.get('display_name') || '').trim();
    const email = String(data.get('email') || '').trim().toLowerCase();

    if (!content || !displayName || !email) {
      return this.#showFormError(error, 'Please fill in all required fields.');
    }
    if (this.#submittedQuestionEmails.has(email)) {
      return this.#showFormError(error, 'You have already submitted a question for this product.');
    }

    const restore = this.#pending(submit);
    try {
      await submitQuestion(this.#appKey, this.#bundle.productId, this.#bundle.productTitle, { content, email, display_name: displayName });
      this.#submittedQuestionEmails.add(email);
      success.hidden = false;
      form.reset();
      setTimeout(() => this.#closeDialog(form), 2000);
    } catch (submitError) {
      this.#showFormError(error, emailAwareMessage(submitError));
    } finally {
      restore();
    }
  }

  /** A form-scoped helper element by role. @param {HTMLFormElement} form @param {string} role */
  #formEl(form, role) {
    return /** @type {HTMLElement | null} */ (form.querySelector(`[data-role="${role}"]`));
  }

  /** Disable a submit button + show a sending label; returns a restore fn. @param {HTMLElement} submit */
  #pending(submit) {
    const label = submit.textContent;
    if (submit instanceof HTMLButtonElement) submit.disabled = true;
    submit.textContent = 'Sending…';
    return () => {
      if (submit instanceof HTMLButtonElement) submit.disabled = false;
      submit.textContent = label;
    };
  }

  /** @param {HTMLFormElement} form */
  #resetForm(form) {
    form.reset();
    const input = form.querySelector('input[name="rating"]');
    if (input instanceof HTMLInputElement) input.value = '';
    for (const star of form.querySelectorAll('[data-rating]')) {
      star.classList.remove('caldera-bundle-reviews__star-btn--active');
    }
  }

  /** Close the Horizon dialog-component wrapping a form. @param {HTMLFormElement} form */
  #closeDialog(form) {
    const dialog = form.closest('dialog-component');
    if (dialog && 'closeDialog' in dialog && typeof dialog.closeDialog === 'function') dialog.closeDialog();
  }

  /** @param {HTMLElement} el @param {string} message */
  #showFormError(el, message) {
    el.textContent = message;
    el.hidden = false;
  }

  /** @param {'loading' | 'content' | 'error'} state */
  #setState(state) {
    this.refs.loading.hidden = state !== 'loading';
    this.refs.content.hidden = state !== 'content';
    this.refs.errorState.hidden = state !== 'error';
    // With no reviews at all, the whole section is pointless — hide it.
    if (state === 'error') this.hidden = true;
  }
}

if (!customElements.get('caldera-bundle-reviews')) {
  customElements.define('caldera-bundle-reviews', CalderaBundleReviews);
}

// --- Yotpo API ------------------------------------------------------------

/**
 * @typedef {object} BundleData
 * @property {number} productId
 * @property {string} productTitle
 * @property {{ id: number, title: string }[]} childProducts
 *
 * @typedef {object} AggregatedReview
 * @property {number} product_id
 * @property {string} product_title
 * @property {number} score
 * @property {string} created_at
 * @property {number} [votes_up]
 * @property {number} [votes_down]
 * @property {string} [title]
 * @property {string} [content]
 * @property {boolean} [verified_buyer]
 * @property {{ display_name: string }} user
 *
 * @typedef {object} AggregatedQuestion
 * @property {string} product_title
 * @property {string} content
 * @property {string} created_at
 * @property {number} [votes_up]
 * @property {number} [votes_down]
 * @property {{ display_name: string }} [asker]
 * @property {{ content: string, created_at: string, user?: { display_name: string } }[]} [sorted_public_answers]
 */

/** @param {string} appKey @param {number} productId @param {number} page @param {number} perPage */
async function fetchReviewsPage(appKey, productId, page = 1, perPage = 100) {
  try {
    // V3 storefront API — same source as the star widgets (see caldera-bundle-rating.js).
    // The v1 API excluded Shop-app-collected reviews, so the list undercounted the stars.
    const response = await fetch(
      `https://api-cdn.yotpo.com/v3/storefront/store/${appKey}/product/${productId}/reviews?page=${page}&perPage=${perPage}`
    );
    if (!response.ok) return { reviews: [] };
    const json = await response.json();
    const reviews = (json?.reviews ?? []).map(normalizeV3Review);
    const pagination = json?.pagination
      ? { page: json.pagination.page, per_page: json.pagination.perPage, total: json.pagination.total }
      : undefined;
    return { reviews, pagination };
  } catch {
    return { reviews: [] };
  }
}

/**
 * Map a V3 review to the snake_case shape the render pipeline was built on
 * (v1's), so the endpoint swap stays local to the fetch layer.
 *
 * @param {Record<string, any>} review
 */
function normalizeV3Review(review) {
  return {
    id: review.id,
    score: review.score,
    title: review.title,
    content: review.content,
    created_at: review.createdAt,
    verified_buyer: review.verifiedBuyer,
    votes_up: review.votesUp,
    votes_down: review.votesDown,
    user: { display_name: review.user?.displayName ?? '' },
  };
}

/** @param {string} appKey @param {{ id: number, title: string }} product */
async function fetchAllReviews(appKey, product) {
  const first = await fetchReviewsPage(appKey, product.id, 1, 100);
  const reviews = [...first.reviews];
  if (first.pagination) {
    const totalPages = Math.ceil(first.pagination.total / first.pagination.per_page);
    if (totalPages > 1) {
      const pages = [];
      for (let page = 2; page <= totalPages; page++) pages.push(fetchReviewsPage(appKey, product.id, page, 100));
      for (const data of await Promise.all(pages)) reviews.push(...data.reviews);
    }
  }
  return reviews.map((review) => ({ ...review, product_id: product.id, product_title: product.title }));
}

/** @param {string} appKey @param {{ id: number, title: string }[]} products @returns {Promise<AggregatedReview[]>} */
async function fetchCombinedReviews(appKey, products) {
  const results = await Promise.all(products.map((product) => fetchAllReviews(appKey, product)));
  const reviews = results.flat();
  return reviews.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
}

/** @param {string} appKey @param {number} productId @param {number} page @param {number} perPage */
async function fetchQuestionsPage(appKey, productId, page = 1, perPage = 100) {
  try {
    const response = await fetch(`https://api.yotpo.com/products/${appKey}/${productId}/questions?page=${page}&per_page=${perPage}`);
    if (!response.ok) return { questions: [] };
    const json = await response.json();
    const total = json?.response?.total_questions;
    const currentPage = Number.parseInt(json?.response?.page, 10) || page;
    const itemsPerPage = Number.parseInt(json?.response?.per_page, 10) || perPage;
    return {
      questions: json?.response?.questions ?? [],
      pagination: total ? { page: currentPage, per_page: itemsPerPage, total } : undefined,
    };
  } catch {
    return { questions: [] };
  }
}

/** @param {string} appKey @param {{ id: number, title: string }} product */
async function fetchAllQuestions(appKey, product) {
  const first = await fetchQuestionsPage(appKey, product.id, 1, 100);
  const questions = [...first.questions];
  if (first.pagination) {
    const totalPages = Math.ceil(first.pagination.total / first.pagination.per_page);
    if (totalPages > 1) {
      const pages = [];
      for (let page = 2; page <= totalPages; page++) pages.push(fetchQuestionsPage(appKey, product.id, page, 100));
      for (const data of await Promise.all(pages)) questions.push(...data.questions);
    }
  }
  return questions.map((question) => ({ ...question, product_id: product.id, product_title: product.title }));
}

/** @param {string} appKey @param {{ id: number, title: string }[]} products @returns {Promise<AggregatedQuestion[]>} */
async function fetchCombinedQuestions(appKey, products) {
  const results = await Promise.all(products.map((product) => fetchAllQuestions(appKey, product)));
  const questions = results.flat();
  return questions.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
}

/** @param {string} appKey @param {number} productId @param {{ rating: number, title: string, content: string, email: string, display_name: string }} data */
async function submitReview(appKey, productId, data) {
  const response = await fetch('https://api.yotpo.com/v1/widget/reviews', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      appkey: appKey,
      domain: window.location.hostname,
      sku: String(productId),
      product_title: document.title,
      product_url: window.location.href,
      display_name: data.display_name,
      email: data.email,
      review_content: data.content,
      review_title: data.title,
      review_score: data.rating,
    }),
  });
  await assertYotpoOk(response);
  return true;
}

/** @param {string} appKey @param {number} productId @param {string} productTitle @param {{ content: string, email: string, display_name: string }} data */
async function submitQuestion(appKey, productId, productTitle, data) {
  const response = await fetch('https://api.yotpo.com/questions/send_confirmation_mail', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      appkey: appKey,
      sku: String(productId),
      product_title: productTitle,
      product_url: window.location.href,
      display_name: data.display_name,
      email: data.email,
      question_content: data.content,
      domain: window.location.hostname,
    }),
  });
  await assertYotpoOk(response);
  return true;
}

/** Throws with Yotpo's message when a submit response is not a success. @param {Response} response */
async function assertYotpoOk(response) {
  const result = await response.json();
  if (typeof result !== 'object' || result === null) throw new Error('Invalid response from server');
  const code = result.code || result.status?.code;
  const message = result.message || result.status?.message;
  if (!response.ok || (code && code !== 200)) throw new Error(message || 'Unknown error');
}

// --- Sorting --------------------------------------------------------------

/** @param {AggregatedReview[]} reviews @param {string} sortBy */
function sortReviews(reviews, sortBy) {
  const sorted = [...reviews];
  switch (sortBy) {
    case 'date-asc':
      return sorted.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    case 'rating-desc':
      return sorted.sort((a, b) => b.score - a.score);
    case 'rating-asc':
      return sorted.sort((a, b) => a.score - b.score);
    case 'helpful':
      return sorted.sort((a, b) => helpfulScore(b) - helpfulScore(a));
    case 'date-desc':
    default:
      return sorted.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }
}

/** @param {AggregatedQuestion[]} questions @param {string} sortBy */
function sortQuestions(questions, sortBy) {
  const sorted = [...questions];
  switch (sortBy) {
    case 'date-asc':
      return sorted.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    case 'helpful':
      return sorted.sort((a, b) => helpfulScore(b) - helpfulScore(a));
    case 'date-desc':
    default:
      return sorted.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }
}

/** @param {{ votes_up?: number, votes_down?: number }} item */
function helpfulScore(item) {
  return (item.votes_up ?? 0) - (item.votes_down ?? 0);
}

// --- Rendering helpers ----------------------------------------------------

/** @param {string} className @param {string} [text] */
function el(className, text) {
  const node = document.createElement('div');
  node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

/** @param {AggregatedReview} review */
function reviewCard(review) {
  const card = el('caldera-bundle-reviews__card');
  card.append(el('caldera-bundle-reviews__card-product', review.product_title));

  const header = el('caldera-bundle-reviews__card-header');
  const stars = el('caldera-bundle-reviews__stars');
  stars.append(...starElements(review.score));
  stars.setAttribute('aria-label', `${review.score.toFixed(1)} out of 5 stars`);
  header.append(stars, el('caldera-bundle-reviews__card-date', relativeDate(review.created_at)));
  card.append(header);

  const author = el('caldera-bundle-reviews__card-author', review.user?.display_name || 'Anonymous');
  if (review.verified_buyer) {
    const verified = document.createElement('span');
    verified.className = 'caldera-bundle-reviews__verified';
    verified.textContent = 'Verified Buyer';
    author.append(' ', verified);
  }
  card.append(author);

  if (review.title?.trim()) {
    const title = document.createElement('h4');
    title.className = 'caldera-bundle-reviews__card-title';
    title.textContent = decodeEntities(review.title);
    card.append(title);
  }
  if (review.content?.trim()) {
    const content = document.createElement('p');
    content.className = 'caldera-bundle-reviews__card-content';
    content.textContent = decodeEntities(review.content);
    card.append(content);
  }
  return card;
}

/** @param {AggregatedQuestion} question */
function questionCard(question) {
  const card = el('caldera-bundle-reviews__card');
  card.append(el('caldera-bundle-reviews__card-product', question.product_title));

  const header = el('caldera-bundle-reviews__card-header');
  header.append(
    el('caldera-bundle-reviews__card-author', question.asker?.display_name || 'Anonymous'),
    el('caldera-bundle-reviews__card-date', absoluteDate(question.created_at))
  );
  card.append(header, el('caldera-bundle-reviews__question', `Q: ${decodeEntities(question.content)}`));

  for (const answer of question.sorted_public_answers ?? []) {
    card.append(el('caldera-bundle-reviews__answer', `A: ${decodeEntities(answer.content)}`));
    const author = answer.user?.display_name || 'Caldera + Lab';
    card.append(el('caldera-bundle-reviews__answer-meta', `By ${author} ${absoluteDate(answer.created_at)}`));
  }

  const helpful = helpfulScore(question);
  if (question.votes_up != null && question.votes_down != null) {
    card.append(
      el('caldera-bundle-reviews__helpful', `${helpful} ${Math.abs(helpful) === 1 ? 'person' : 'people'} found this helpful`)
    );
  }
  return card;
}

/** Quarter-rounded 0–5 rating → 5 star <span>s. @param {number} rating */
function starElements(rating) {
  const rounded = Math.round(rating * 4) / 4;
  const whole = Math.floor(rounded);
  const decimal = rounded - whole;
  const full = whole + (decimal >= 0.75 ? 1 : 0);
  const half = decimal >= 0.25 && decimal < 0.75 ? 1 : 0;
  const empty = 5 - full - half;

  /** @type {HTMLSpanElement[]} */
  const stars = [];
  /** @param {string} modifier */
  const push = (modifier) => {
    const span = document.createElement('span');
    span.className = `caldera-bundle-reviews__star${modifier ? ` caldera-bundle-reviews__star--${modifier}` : ''}`;
    span.textContent = '★';
    span.setAttribute('aria-hidden', 'true');
    stars.push(span);
  };
  for (let i = 0; i < full; i++) push('');
  for (let i = 0; i < half; i++) push('half');
  for (let i = 0; i < empty; i++) push('empty');
  return stars;
}

/** @param {string} message */
function emptyMessage(message) {
  return el('caldera-bundle-reviews__empty', message);
}

function spinner() {
  const wrap = el('caldera-bundle-reviews__loading');
  wrap.append(el('caldera-bundle-reviews__spinner'));
  return wrap;
}

/**
 * @param {{ container: HTMLElement, pageNumbers: HTMLElement, prev: HTMLButtonElement,
 *   next: HTMLButtonElement, page: number, totalPages: number, onSelect: (page: number) => void }} options
 */
function renderPagination({ container, pageNumbers, prev, next, page, totalPages, onSelect }) {
  if (totalPages <= 1) {
    container.hidden = true;
    return;
  }
  container.hidden = false;
  prev.disabled = page === 1;
  next.disabled = page === totalPages;

  const buttons = paginationRange(page, totalPages).map((entry) => {
    const button = document.createElement('button');
    button.type = 'button';
    if (entry === '…') {
      button.className = 'caldera-bundle-reviews__page caldera-bundle-reviews__page--ellipsis';
      button.textContent = '…';
      button.disabled = true;
    } else {
      button.className = `caldera-bundle-reviews__page${entry === page ? ' caldera-bundle-reviews__page--active' : ''}`;
      button.textContent = String(entry);
      button.addEventListener('click', () => onSelect(entry));
    }
    return button;
  });
  pageNumbers.replaceChildren(...buttons);
}

/** @param {number} page @param {number} total @returns {(number | '…')[]} */
function paginationRange(page, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  /** @type {(number | '…')[]} */
  const pages = [1];
  if (page > 3) pages.push('…');
  for (let i = Math.max(2, page - 1); i <= Math.min(total - 1, page + 1); i++) pages.push(i);
  if (page < total - 2) pages.push('…');
  pages.push(total);
  return pages;
}

// --- Formatting -----------------------------------------------------------

/** @param {string} text */
function decodeEntities(text) {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

/** @param {string} dateString */
function relativeDate(dateString) {
  const days = Math.floor((Date.now() - new Date(dateString).getTime()) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  if (days < 365) {
    const months = Math.floor(days / 30);
    return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  }
  const years = Math.floor(days / 365);
  return `${years} ${years === 1 ? 'year' : 'years'} ago`;
}

/** @param {string} dateString */
function absoluteDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

/** @param {unknown} error @param {string} email @param {Set<string>} submitted */
function reviewSubmitMessage(error, email, submitted) {
  const message = error instanceof Error ? error.message : 'Unknown error';
  if (message === 'duplicate_review') {
    submitted.add(email);
    return 'You have already submitted a review for this product.';
  }
  return emailAwareMessage(error);
}

/** @param {unknown} error */
function emailAwareMessage(error) {
  const message = (error instanceof Error ? error.message : 'Unknown error').toLowerCase();
  if (message.includes('account') || message.includes('recordnotfound') || message.includes('not found')) {
    return 'There was an issue with your email address. Please check and try again.';
  }
  return `Error: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`;
}
